import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../../core/permissons/CheckPermissons';
import { showError, showSuccessAlert } from '../../../../../core/alert/sweetAlertConfig';
import GateManagementLayout from '../GateManagementLayout';
import { GateModal, DeleteGateModal, ViewGateModal } from './GateModals';

interface Gate {
    id: number;
    location?: { id: number; name: string };
    name: string;
    description: string;
    relayDevice?: { id: number; name: string };
    relayChannelNumber: number;
    entryCamera?: { id: number; name: string };
    exitCamera?: { id: number; name: string };
}

const Gates: React.FC = () => {
    const [gates, setGates] = useState<Gate[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Dropdown data
    const [locations, setLocations] = useState<any[]>([]);
    const [relays, setRelays] = useState<any[]>([]);
    const [cameras, setCameras] = useState<any[]>([]);
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedGate, setSelectedGate] = useState<Gate | null>(null);
    
    const [formData, setFormData] = useState({
        locationId: '',
        name: '',
        description: '',
        relayDeviceId: '',
        relayChannelNumber: '',
        entryCameraId: '',
        exitCameraId: ''
    });

    const canManage = checkPermission('ROLE_MANAGE_GATE');

    useEffect(() => {
        fetchGates();
        fetchDropdownData();
    }, []);

    const fetchGates = async () => {
        setLoading(true);
        try {
            const response = await apiDefinitions.getGates(0, 50, {});
            if (response.data.status === 'S1000') {
                setGates(response.data.data || []);
            } else {
                showError(response.data.statusDescription || 'Failed to fetch gates');
            }
        } catch (error: any) {
            showError('Error fetching gates: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            // Fetch Locations
            const locRes = await apiDefinitions.getLocations("1", { filterType: "", filterValue: "", date: "" });
            if (locRes.status === 200) {
                setLocations(locRes.data.data.locations || []);
            }

            // Fetch Relays
            const relayRes = await apiDefinitions.getRelayDevices(0, 100, {});
            if (relayRes.data.status === 'S1000') {
                setRelays(relayRes.data.data || []);
            }

            // Fetch Cameras
            const camRes = await apiDefinitions.getCameras(0, 100, {});
            if (camRes.data.status === 'S1000') {
                setCameras(camRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching dropdown data', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openAddModal = () => {
        setIsEdit(false);
        setFormData({
            locationId: '',
            name: '',
            description: '',
            relayDeviceId: '',
            relayChannelNumber: '',
            entryCameraId: '',
            exitCameraId: ''
        });
        setShowModal(true);
    };

    const openEditModal = (gate: Gate) => {
        setIsEdit(true);
        setSelectedGate(gate);
        setFormData({
            locationId: gate.location?.id?.toString() || '',
            name: gate.name,
            description: gate.description || '',
            relayDeviceId: gate.relayDevice?.id?.toString() || '',
            relayChannelNumber: gate.relayChannelNumber.toString(),
            entryCameraId: gate.entryCamera?.id?.toString() || '',
            exitCameraId: gate.exitCamera?.id?.toString() || ''
        });
        setShowModal(true);
    };

    const openDeleteModal = (gate: Gate) => {
        setSelectedGate(gate);
        setShowDeleteModal(true);
    };

    const openViewModal = (gate: Gate) => {
        setSelectedGate(gate);
        setShowViewModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                locationId: formData.locationId ? parseInt(formData.locationId) : 0,
                name: formData.name,
                description: formData.description,
                relayDeviceId: formData.relayDeviceId ? parseInt(formData.relayDeviceId) : 0,
                relayChannelNumber: formData.relayChannelNumber ? parseInt(formData.relayChannelNumber) : 0,
                entryCameraId: formData.entryCameraId ? parseInt(formData.entryCameraId) : null,
                exitCameraId: formData.exitCameraId ? parseInt(formData.exitCameraId) : null
            };

            if (!payload.locationId || !payload.name || !payload.relayDeviceId) {
                showError('Please fill all required fields correctly');
                setLoading(false);
                return;
            }

            if (isEdit && selectedGate) {
                const response = await apiDefinitions.updateGate({ ...payload, id: selectedGate.id });
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Gate updated successfully');
                    setShowModal(false);
                    fetchGates();
                } else {
                    showError(response.data.statusDescription || 'Failed to update gate');
                }
            } else {
                const response = await apiDefinitions.createGate(payload);
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Gate created successfully');
                    setShowModal(false);
                    fetchGates();
                } else {
                    showError(response.data.statusDescription || 'Failed to create gate');
                }
            }
        } catch (error: any) {
            showError('Error saving gate: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedGate) return;
        setLoading(true);
        try {
            const response = await apiDefinitions.deleteGate(selectedGate.id);
            if (response.data.status === 'S1000') {
                showSuccessAlert('Gate deleted successfully');
                setShowDeleteModal(false);
                fetchGates();
            } else {
                showError(response.data.statusDescription || 'Failed to delete gate');
            }
        } catch (error: any) {
            showError('Error deleting gate: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <GateManagementLayout activeTab="gates">
            <div className="card mt-2">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">System Gates</h5>
                    {canManage && (
                        <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                            <i className="ti ti-plus me-1"></i> New Gate
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control w-25"
                            placeholder="Search gates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive">
                        <Table className="table table-borderless table-striped">
                            <thead className="table-light">
                                <tr>
                                    <th>Gate Name</th>
                                    <th>Location</th>
                                    <th>Hardware (Relay/Ch)</th>
                                    <th>Cameras (In/Out)</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && gates.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center">Loading...</td></tr>
                                ) : gates.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center">No gates configured</td></tr>
                                ) : (
                                    gates.filter(g => (g.name || '').toLowerCase().includes(searchTerm.toLowerCase())).map(gate => (
                                        <tr key={gate.id}>
                                            <td className="fw-bold text-primary">{gate.name}</td>
                                            <td>{gate.location?.name || 'N/A'}</td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span>{gate.relayDevice?.name || 'Unlinked'}</span>
                                                    <small className="text-muted">Channel: {gate.relayChannelNumber}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="hstack gap-2">
                                                    <span className={`badge bg-${gate.entryCamera ? 'success' : 'light'} text-${gate.entryCamera ? 'white' : 'muted'}`} title="Entry Cam">
                                                        {gate.entryCamera ? 'IN' : 'NO IN'}
                                                    </span>
                                                    <span className={`badge bg-${gate.exitCamera ? 'info' : 'light'} text-${gate.exitCamera ? 'white' : 'muted'}`} title="Exit Cam">
                                                        {gate.exitCamera ? 'OUT' : 'NO OUT'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <div className="hstack gap-2 justify-content-end">
                                                    <button 
                                                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill" 
                                                        onClick={() => openViewModal(gate)}
                                                        title="View"
                                                    >
                                                        <i className="feather-eye" />
                                                    </button>
                                                    {canManage && (
                                                        <>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-primary rounded-pill" 
                                                                onClick={() => openEditModal(gate)}
                                                            >
                                                                <i className="feather-edit" />
                                                            </button>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-danger rounded-pill" 
                                                                onClick={() => openDeleteModal(gate)}
                                                            >
                                                                <i className="feather-trash-2" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <GateModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={handleSubmit}
                formData={formData}
                handleInputChange={handleInputChange}
                locations={locations}
                relays={relays}
                entryCameras={cameras.filter(c => c.role === 'ENTRY')}
                exitCameras={cameras.filter(c => c.role === 'EXIT')}
                loading={loading}
                isEdit={isEdit}
            />

            <DeleteGateModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                gateName={selectedGate?.name || ''}
                loading={loading}
            />

            <ViewGateModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                gate={selectedGate}
            />
        </GateManagementLayout>
    );
};

export default Gates;
